/**
 * Eva platform-wide budget defaults API client (SF-3839).
 *
 * Wraps the admin platform-defaults endpoints on sf-api so the new "Platform
 * Defaults" tab under Eva can READ and WRITE the GLOBAL default monthly Eva
 * caps (company + per-user) — the single source of truth that sf-api publishes
 * to the shared Valkey keys and that sf-chat-service enforces. Mirrors the
 * data-access pattern in evaBudgetClient.ts (per-company override).
 *
 *   GET /admin/eva/budget-defaults  — perm admin:eva:platform:read
 *   PUT /admin/eva/budget-defaults  — perm admin:eva:platform:write
 *     body { companyMonthlyCapUsd: number, userMonthlyCapUsd: number, note?: string }
 *
 * ⚠️ sf-api wraps every response in `{ success, data }`. The Refine
 * `dataProvider.custom` unwraps the envelope, so `useCustom` exposes the inner
 * payload via `query.data?.data` (same convention as the usage/budget hooks).
 * Money values are fixed-precision STRINGS to avoid float drift — parse only at
 * the edge.
 */
import { useCustom, useCustomMutation } from '@refinedev/core';

/** Base path for the Eva platform-defaults endpoints on sf-api. */
export const EVA_BUDGET_DEFAULTS_BASE = 'admin/eva/budget-defaults';

/**
 * Permission gating platform-default READS (matches sf-api requirePermissions).
 * Platform defaults are global, so they use the super-admin-only
 * `admin:eva:platform:*` scope — distinct from the company-admin-holdable
 * `admin:eva:budget:*` per-company/per-user OVERRIDE scope (SF-3839).
 */
export const EVA_PLATFORM_READ_PERMISSION = 'admin:eva:platform:read';

/** Permission gating platform-default WRITES (matches sf-api requirePermissions). */
export const EVA_PLATFORM_WRITE_PERMISSION = 'admin:eva:platform:write';

/**
 * Platform-defaults envelope returned by GET/PUT (mirrors sf-api
 * services/insights/evaBudgetPlatformDefaultService.js `shapeDefaults`).
 * Money values are fixed-precision strings.
 */
export interface EvaBudgetPlatformDefaults {
  companyMonthlyCapUsd: string;
  userMonthlyCapUsd: string;
  updatedBy: string | null;
  updatedAt: string | null;
  note: string | null;
}

/** Body for PUT — both caps are required, non-negative finite numbers. */
export interface EvaBudgetPlatformDefaultsUpdate {
  companyMonthlyCapUsd: number;
  userMonthlyCapUsd: number;
  note?: string;
}

/** Common options shared by the hooks. */
interface EnabledOption {
  /** Gate the request (e.g. on access-control resolution). Defaults to true. */
  enabled?: boolean;
}

/**
 * Fetch the global platform default caps (company + per-user).
 * Returns the inner payload via `query.data?.data` (sf-api envelope unwrapped
 * by the data provider).
 */
export function useEvaBudgetPlatformDefaults(options: EnabledOption = {}) {
  return useCustom<EvaBudgetPlatformDefaults>({
    url: `/${EVA_BUDGET_DEFAULTS_BASE}`,
    method: 'get',
    queryOptions: {
      enabled: options.enabled ?? true,
      // Don't refetch on window focus: a background refetch would re-seed the
      // edit fields and silently discard an admin's unsaved edits when they tab
      // away and back. Values (re)load on mount and after an explicit save.
      refetchOnWindowFocus: false,
    },
    // The page surfaces read failures inline (a missing read perm is a 403), so
    // suppress the global error toast for this query.
    errorNotification: false,
  });
}

/**
 * Mutation to update the global platform default caps. The caller supplies the
 * body at call time; on success the page refetches. Errors (including a 403 for
 * callers lacking `admin:eva:platform:write`) are surfaced by the caller, so the
 * global error toast is suppressed here.
 */
export function useSetEvaBudgetPlatformDefaults() {
  return useCustomMutation<EvaBudgetPlatformDefaults>();
}

/**
 * Build the PUT mutation args for a platform-defaults write. Extracted so
 * callers and tests share one source of truth for the URL + method + body shape.
 * @param body - the update payload (both caps required)
 */
export function platformDefaultsUpdateMutationArgs(body: EvaBudgetPlatformDefaultsUpdate) {
  return {
    url: `/${EVA_BUDGET_DEFAULTS_BASE}`,
    method: 'put' as const,
    values: body,
    // Failures are surfaced inline by the page (esp. 403); no global toast.
    errorNotification: false as const,
    successNotification: false as const,
  };
}
